import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Quiz as QuizIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ClubService from '../services/clubService';

interface QuizQuestion {
  question: string;
  type?: 'mcq' | 'paragraph';
  options: string[];
  correctAnswer: number;
}

interface QuizData {
  passingScore: number;
  questions: QuizQuestion[];
}

const QuizPage: React.FC = () => {
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState<QuizData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [paragraphAnswers, setParagraphAnswers] = useState<string[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [paragraphAnswer, setParagraphAnswer] = useState<string>('');
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [quizStarted, setQuizStarted] = useState(false);

  useEffect(() => {
    if (clubId) {
      loadQuiz();
    }
  }, [clubId]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (quizStarted && timeLeft > 0) {
      timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    } else if (timeLeft === 0 && quizStarted) {
      handleSubmitQuiz();
    }
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, quizStarted]);

  const loadQuiz = async () => {
    try {
      setLoading(true);
      const quizData = await ClubService.getClubQuiz(clubId!);
      setQuiz(quizData);
      setAnswers(new Array(quizData.questions.length).fill(-1));
      setParagraphAnswers(new Array(quizData.questions.length).fill(''));
    } catch (error: any) {
      // Check if this is an application status error
      if (error.response?.data?.applicationStatus) {
        const appStatus = error.response.data.applicationStatus;
        setError(`You already have a ${appStatus} application for this club. Your quiz score: ${error.response.data.percentage}%`);
      } else {
        setError(error.response?.data?.message || 'Failed to load quiz. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = () => {
    setQuizStarted(true);
  };

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
  };

  const handleNextQuestion = () => {
    const currentQuizQuestion = quiz!.questions[currentQuestion];

    // Check if answer is provided based on question type
    const hasAnswer = currentQuizQuestion.type === 'paragraph'
      ? paragraphAnswer.trim() !== ''
      : selectedAnswer !== null;

    if (hasAnswer) {
      if (currentQuizQuestion.type === 'paragraph') {
        const newParagraphAnswers = [...paragraphAnswers];
        newParagraphAnswers[currentQuestion] = paragraphAnswer;
        setParagraphAnswers(newParagraphAnswers);
        setParagraphAnswer('');
      } else {
        const newAnswers = [...answers];
        newAnswers[currentQuestion] = selectedAnswer!;
        setAnswers(newAnswers);
        setSelectedAnswer(null);
      }

      if (currentQuestion < quiz!.questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        // Load existing answer if going back
        if (quiz!.questions[currentQuestion + 1].type === 'paragraph') {
          setParagraphAnswer(paragraphAnswers[currentQuestion + 1] || '');
        } else {
          setSelectedAnswer(answers[currentQuestion + 1] !== -1 ? answers[currentQuestion + 1] : null);
        }
      } else {
        handleSubmitQuiz();
      }
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestion > 0) {
      // Save current answer before going back
      const currentQuizQuestion = quiz!.questions[currentQuestion];
      if (currentQuizQuestion.type === 'paragraph' && paragraphAnswer.trim()) {
        const newParagraphAnswers = [...paragraphAnswers];
        newParagraphAnswers[currentQuestion] = paragraphAnswer;
        setParagraphAnswers(newParagraphAnswers);
      } else if (selectedAnswer !== null) {
        const newAnswers = [...answers];
        newAnswers[currentQuestion] = selectedAnswer;
        setAnswers(newAnswers);
      }

      setCurrentQuestion(currentQuestion - 1);
      // Load previous answer
      if (quiz!.questions[currentQuestion - 1].type === 'paragraph') {
        setParagraphAnswer(paragraphAnswers[currentQuestion - 1] || '');
        setSelectedAnswer(null);
      } else {
        setSelectedAnswer(answers[currentQuestion - 1] !== -1 ? answers[currentQuestion - 1] : null);
        setParagraphAnswer('');
      }
    }
  };

  const handleSubmitQuiz = async () => {
    try {
      setLoading(true);

      // Save current answer before submitting
      const currentQuizQuestion = quiz!.questions[currentQuestion];
      let finalAnswers = [...answers];
      let finalParagraphAnswers = [...paragraphAnswers];

      if (currentQuizQuestion.type === 'paragraph' && paragraphAnswer.trim()) {
        finalParagraphAnswers[currentQuestion] = paragraphAnswer;
      } else if (selectedAnswer !== null) {
        finalAnswers[currentQuestion] = selectedAnswer;
      }

      // Combine MCQ and paragraph answers into a single array
      // For each question, use paragraph answer if it's a paragraph type, otherwise use MCQ answer
      const combinedAnswers = quiz!.questions.map((q, index) => {
        if (q.type === 'paragraph') {
          return finalParagraphAnswers[index] || '';
        } else {
          return finalAnswers[index];
        }
      });

      await ClubService.submitApplication(clubId!, {
        answers: combinedAnswers,
        motivation: 'Automatically submitted after quiz completion' // Default motivation
      });

      // Always redirect to application submitted page regardless of score
      navigate('/pending-approval', {
        state: {
          type: 'club-application',
          clubId: clubId,
        }
      });
    } catch (error: any) {
      setError('Failed to submit quiz. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return <LoadingSpinner fullScreen message="Loading quiz..." />;
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Container>
    );
  }

  if (!quiz) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          No quiz available for this club.
        </Alert>
        <Button variant="contained" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Container>
    );
  }


  if (!quizStarted) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <QuizIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />

            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Club Entry Quiz
            </Typography>

            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              This quiz contains {quiz.questions.length} questions.
              You have 5 minutes to complete the quiz.
            </Typography>

            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <strong>Instructions:</strong>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Read each question carefully</li>
                <li>Select the best answer from the options provided</li>
                <li>You can navigate back to previous questions</li>
                <li>Submit your quiz before time runs out</li>
              </ul>
            </Alert>

            <Button
              variant="contained"
              size="large"
              onClick={handleStartQuiz}
              startIcon={<TimerIcon />}
            >
              Start Quiz
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  const currentQuizQuestion = quiz.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / quiz.questions.length) * 100;
  const questionType = currentQuizQuestion.type || 'mcq'; // Default to mcq if type is not set

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Quiz Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Question {currentQuestion + 1} of {quiz.questions.length}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimerIcon color={timeLeft < 60 ? 'error' : 'primary'} />
              <Typography
                variant="h6"
                color={timeLeft < 60 ? 'error.main' : 'primary.main'}
                sx={{ fontWeight: 600 }}
              >
                {formatTime(timeLeft)}
              </Typography>
            </Box>
          </Box>
          <LinearProgress variant="determinate" value={progress} sx={{ mb: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {Math.round(progress)}% Complete
          </Typography>
        </CardContent>
      </Card>

      {/* Question Card */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {currentQuizQuestion.question}
              </Typography>
            </FormLabel>

            {questionType === 'paragraph' ? (
              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="Type your answer here..."
                value={paragraphAnswer}
                onChange={(e) => setParagraphAnswer(e.target.value)}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1rem',
                    lineHeight: 1.6,
                  }
                }}
              />
            ) : (
              <RadioGroup
                value={selectedAnswer}
                onChange={(e) => handleAnswerSelect(parseInt(e.target.value))}
              >
                {currentQuizQuestion.options.map((option, index) => (
                  <FormControlLabel
                    key={index}
                    value={index}
                    control={<Radio />}
                    label={option}
                    sx={{
                      mb: 1,
                      p: 1,
                      border: '1px solid',
                      borderColor: selectedAnswer === index ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            )}
          </FormControl>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handlePreviousQuestion}
              disabled={currentQuestion === 0}
            >
              Previous
            </Button>

            <Button
              variant="contained"
              onClick={handleNextQuestion}
              disabled={
                questionType === 'paragraph'
                  ? paragraphAnswer.trim() === ''
                  : selectedAnswer === null
              }
            >
              {currentQuestion === quiz.questions.length - 1 ? 'Submit Quiz' : 'Next'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Time Warning Dialog */}
      <Dialog open={timeLeft <= 60 && timeLeft > 0}>
        <DialogTitle>Time Warning</DialogTitle>
        <DialogContent>
          <Typography>
            You have less than 1 minute remaining! Please complete your quiz soon.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {}}>Continue</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default QuizPage;